/**
 * Hooks for the audit console's drawers and modals. They load the detail data through the
 * fetchers the console controller passes down; the views only render what these return.
 */
import { useEffect, useState } from 'react';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { formatPriceName } from '../../../utils/priceName';
import { getAuditImageUrl } from '../../../utils/barcodeValidation';
import { normalizeAuditCurrency } from './AuditPriceDisplay';

/** Loads one audit record (scan) while the record drawer is open; supports the Bs correction. */
export function useAuditRecord({ open, recordId, onFetchRecord, onCorrectCurrency }) {
    const [loading, setLoading] = useState(false);
    const [record, setRecord] = useState(null);

    useEffect(() => {
        if (!open || !recordId) {
            setRecord(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await onFetchRecord(recordId);
                if (!cancelled) setRecord(data);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, recordId, onFetchRecord]);

    const handleCorrectCurrency = async () => {
        const updated = await onCorrectCurrency?.(record);
        if (updated) setRecord(updated);
    };

    return {
        loading,
        record,
        images: (record?.images || []).map(getAuditImageUrl).filter(Boolean),
        productName: record?.auditProduct ? formatPriceName(record.auditProduct) : null,
        hasFlags: record?.flags && Object.values(record.flags).some(Boolean),
        barcodeInvalid: record?.barcodeMeta && !record.barcodeMeta.valid,
        canCorrectToBs: record && normalizeAuditCurrency(record.currency) === 'usd' && record.price != null,
        isBs: record && normalizeAuditCurrency(record.currency) === 'bs',
        hint: record?.currencyCorrectionHint,
        handleCorrectCurrency,
    };
}

/** Loads a store check (visit) and its scans while the visit drawer is open. */
export function useStoreCheckRecords({ open, storeCheckId, onFetchRecords }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!open || !storeCheckId) {
            setData(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const result = await onFetchRecords(storeCheckId);
                if (!cancelled) setData(result);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, storeCheckId, onFetchRecords]);

    return { loading, storeCheck: data?.storeCheck, records: data?.records || [] };
}

/** Loads an auditor's 30-day stats and recent activity while the auditor drawer is open. */
export function useAuditorProfile({ open, userId, onFetchStats, onFetchActivity }) {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [timeline, setTimeline] = useState([]);

    useEffect(() => {
        if (!open || !userId) {
            setStats(null);
            setTimeline([]);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [statsRes, activityRes] = await Promise.all([onFetchStats(userId), onFetchActivity(userId)]);
                if (!cancelled) {
                    setStats(statsRes);
                    setTimeline(activityRes?.timeline || []);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, userId, onFetchStats, onFetchActivity]);

    return { loading, stats, timeline };
}

/** Form state for the audit-product drawer: fills the form from the product and normalizes the submit. */
export function useAuditProductForm({ open, product, onSave }) {
    const [form] = Form.useForm();

    useEffect(() => {
        if (!open || !product) return;
        form.setFieldsValue({
            name: product.name,
            code: product.code,
            segment: product.segment || '',
            category: product.category || '',
            country: product.country || '',
            grams: product.grams,
            price: product.price,
            currency: product.currency || '',
            active: product.active !== false,
            missingData: !!product.missingData,
            status: product.status || 'complete',
            enrichedAt: product.enrichedAt ? dayjs(product.enrichedAt) : null,
            enrichedBy: product.enrichedBy ? String(product.enrichedBy) : '',
            tenantId: product.tenantId ? String(product.tenantId) : '',
        });
    }, [open, product, form]);

    const handleFinish = (values) => {
        const enrichedAt = values.enrichedAt;
        onSave({
            ...values,
            enrichedAt: enrichedAt && enrichedAt.toISOString ? enrichedAt.toISOString() : enrichedAt || null,
        });
    };

    return { form, handleFinish, recentScans: product?.recentScans || [] };
}

/** Canonical-product choice for the merge modal. */
export function useMergeSelection(group, onMerge) {
    const products = group?.products || [];
    const [canonicalId, setCanonicalId] = useState(products[0]?._id);

    const handleOk = () => {
        const duplicateIds = products.filter((p) => String(p._id) !== String(canonicalId)).map((p) => p._id);
        onMerge?.({ canonicalId, duplicateIds });
    };

    return { products, canonicalId, setCanonicalId, handleOk };
}
